import React from "react";
import { Box, Dialog, IconButton } from "@mui/material";
import { isDarkMode } from "../../utils/constants";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const MARKDOWN_LINK_REGEX =
  /\[([^\]]+)]\(((?:https?:\/\/|\/(?!\/))[^\s)]+(?:\s+[^\s)]+)*)\)/g;

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const safeMarkdownUrl = (url) => {
  const sanitizedUrl = String(url).replace(/\s/g, "");
  if (/^https?:\/\//i.test(sanitizedUrl)) return sanitizedUrl;
  if (sanitizedUrl.startsWith("/") && !sanitizedUrl.startsWith("//")) {
    return sanitizedUrl;
  }

  return null;
};

const renderMarkdownLinks = (text) =>
  text.replace(MARKDOWN_LINK_REGEX, (_match, linkText, url) => {
    const sanitizedUrl = safeMarkdownUrl(url);
    if (!sanitizedUrl) return _match;

    const styles = "font-weight: bold; color: #2971D7";
    const isExternal = /^https?:\/\//i.test(sanitizedUrl);
    const targetAttrs = isExternal
      ? ' target="_blank" rel="noopener noreferrer"'
      : "";

    return `<a href="${escapeHtml(sanitizedUrl)}" style="${styles}"${targetAttrs}>${escapeHtml(linkText)}</a>`;
  });

const formatLargeNumbers = (number) => {
  const roundDigits = 0;
  const absNumber = Math.abs(number);
  let formattedNumber;

  if (absNumber >= 1_000_000_000_000) {
    formattedNumber =
      (absNumber / 1_000_000_000_000).toFixed(roundDigits) + "T";
  } else if (absNumber >= 1_000_000_000) {
    formattedNumber = (absNumber / 1_000_000_000).toFixed(roundDigits) + "B";
  } else if (absNumber >= 1_000_000) {
    formattedNumber = (absNumber / 1_000_000).toFixed(roundDigits) + "M";
  } else if (absNumber >= 1_000) {
    formattedNumber = (absNumber / 1_000).toFixed(roundDigits) + "K";
  } else {
    formattedNumber = absNumber.toString();
  }

  return (number < 0 ? "-" : "") + formattedNumber;
};

const RenderResponseString = ({
  messageThread,
  component = "div",
  sx = {},
  compactSpecialSpacing = false,
}) => {
  let concatString = "";
  if (messageThread.message) {
    if (messageThread.advancedFilteringMessage != undefined) {
      concatString = messageThread.message.concat(
        "",
        messageThread.advancedFilteringMessage
      );
    } else {
      concatString = messageThread.message;
    }
  }

  let prHeader = false;
  let isHeader = false;
  const responseLines = concatString.split("\n");

  const specialKindForLine = (line) => {
    const normalized = String(line || "").trim();
    if (normalized.toLowerCase().includes("financial advice")) return "disclaimer";
    if (normalized.startsWith("↪")) return "follow-up";
    return null;
  };

  const isSpacerBeforeSpecialLine = (line, idx) => {
    if (!compactSpecialSpacing) return false;
    if (String(line || "").trim()) return false;

    for (let nextIdx = idx + 1; nextIdx < responseLines.length; nextIdx += 1) {
      if (!String(responseLines[nextIdx] || "").trim()) continue;
      return Boolean(specialKindForLine(responseLines[nextIdx]));
    }

    return false;
  };

  return (
    <Box
      component={component}
      sx={{
        fontFamily:
          'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "24px",
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        "& h3, & h4": {
          whiteSpace: "inherit",
          overflowWrap: "inherit",
          wordBreak: "break-word",
          maxWidth: "100%",
        },
        ...sx,
      }}
    >
      {responseLines.map((str, idx) => {
        if (isSpacerBeforeSpecialLine(str, idx)) return null;

        let formattedStr = str.trim();
        let extraString = "";

        prHeader = isHeader;
        if (formattedStr.startsWith("####")) {
          let endIdx = formattedStr.indexOf("####", 4);
          if (endIdx !== -1) {
            extraString = formattedStr.substring(endIdx + 4);
            formattedStr = formattedStr.substring(4, endIdx);
          } else {
            formattedStr = formattedStr.substring(4);
          }
          formattedStr = `<h4>${formattedStr.trim()}</h4>`;
          formattedStr = formattedStr + extraString;
          isHeader = true;
        } else if (formattedStr.startsWith("###")) {
          let endIdx = formattedStr.indexOf("###", 3);
          if (endIdx !== -1) {
            extraString = formattedStr.substring(endIdx + 3);
            formattedStr = formattedStr.substring(3, endIdx);
          } else {
            formattedStr = formattedStr.substring(3);
          }
          formattedStr = `<h3>${formattedStr.trim()}</h3>`;
          formattedStr = formattedStr + extraString;
          isHeader = true;
        } else {
          isHeader = false;
        }

        formattedStr = formattedStr
          .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
          .replace(/\*(.*?)\*/g, "<em>$1</em>");
        formattedStr = renderMarkdownLinks(formattedStr);
        const responseKind = specialKindForLine(formattedStr);

        return (
          <React.Fragment key={idx}>
            {idx !== 0 &&
              !isHeader &&
              !prHeader &&
              !(compactSpecialSpacing && responseKind) && <br />}
            <span
              data-response-kind={responseKind || undefined}
              dangerouslySetInnerHTML={{ __html: formattedStr }}
            />
          </React.Fragment>
        );
      })}
    </Box>
  );
};

const ComingSoonBadge = ({ size = "sm" }) => {
  const px = size === "md" ? 0.75 : 0.5;
  const py = size === "md" ? 0.25 : 0.25;
  const fontSize = size === "md" ? 11 : 10;

  return (
    <Box
      component="span"
      sx={{
        ml: 2,
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        px,
        py,
        fontSize,
        lineHeight: 1,
        fontWeight: 600,
        letterSpacing: 0.2,
        bgcolor: isDarkMode ? "rgba(83,144,230,0.18)" : "rgba(83,144,230,0.14)",
        color: isDarkMode ? "#BFD6FF" : "#2B5BB8",
        border: `1px solid ${isDarkMode ? "rgba(83,144,230,0.35)" : "rgba(83,144,230,0.45)"}`,
        userSelect: "none",
        whiteSpace: "nowrap",
      }}
      aria-label="Coming soon"
      title="Coming soon"
    >
      Coming&nbsp;soon
    </Box>
  );
};

const ImageLightbox = ({
  open,
  onClose,
  images = [],
  index = 0,
  setIndex,
}) => {
  // keyboard navigation
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && images.length > 1) setIndex((i) => (i - 1 + images.length) % images.length);
      if (e.key === "ArrowRight" && images.length > 1) setIndex((i) => (i + 1) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, images.length, onClose, setIndex]);

  const goPrev = () => images.length > 1 && setIndex((i) => (i - 1 + images.length) % images.length);
  const goNext = () => images.length > 1 && setIndex((i) => (i + 1) % images.length);

  // simple swipe for mobile
  const startXRef = React.useRef(0);
  const onTouchStart = (e) => { startXRef.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - startXRef.current;
    if (Math.abs(dx) > 40) (dx > 0 ? goPrev() : goNext());
  };

  const src = images[index];

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: "black",
          display: "flex",
        },
      }}
    >
      {/* Close */}
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{
          position: "fixed",
          top: 8,
          right: 8,
          bgcolor: "rgba(0,0,0,0.4)",
          "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
          zIndex: 10,
        }}
      >
        <CloseRoundedIcon sx={{ color: "white" }} />
      </IconButton>

      {/* Prev */}
      {images.length > 1 && (
        <IconButton
          aria-label="Previous image"
          onClick={goPrev}
          sx={{
            position: "fixed",
            left: 8,
            top: "50%",
            transform: "translateY(-50%)",
            bgcolor: "rgba(0,0,0,0.4)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
            zIndex: 10,
          }}
        >
          <ChevronLeftIcon sx={{ color: "white" }} />
        </IconButton>
      )}

      {/* Next */}
      {images.length > 1 && (
        <IconButton
          aria-label="Next image"
          onClick={goNext}
          sx={{
            position: "fixed",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            bgcolor: "rgba(0,0,0,0.4)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
            zIndex: 10,
          }}
        >
          <ChevronRightIcon sx={{ color: "white" }} />
        </IconButton>
      )}

      {/* Image */}
      <Box
        onClick={onClose}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        {src && (
          <img
            src={src}
            alt=""
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              userSelect: "none",
              WebkitUserSelect: "none",
              touchAction: "manipulation",
            }}
            onClick={(e) => e.stopPropagation()} // don't close when clicking the image
          />
        )}
      </Box>
    </Dialog>
  );
};


export { formatLargeNumbers, RenderResponseString, ComingSoonBadge, ImageLightbox };
